/**
 * WriterJourneyBreakdown.jsx — Feb 28 2026 user spec:
 *   "Audio minutes and word count should be clickable buttons to show
 *    a page that breaks down the books that make it up of audios that
 *    do and mins that each contributes and the ebooks with individual
 *    word counts that contribute to the whole per book."
 *
 * Route: /writer-journey/breakdown?type=audio|words (default audio)
 *
 * Shows two switchable tabs:
 *   • Audio — every audiobook + cinematic series with episode count
 *             and total minutes contributed
 *   • Words — every standalone ebook/text upload with its word count
 *
 * Tap a row to see the underlying content. Numbers are bold and tabular.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Headphones, Film, BookOpen, FileText, Crown, Sparkles,
  Coins, CheckCircle2, Lock, Loader2,
} from 'lucide-react';
import { authFetch, checkAuth } from '../utils/authUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtMins = (m) => {
  const x = Number(m || 0);
  if (x < 60) return `${x}m`;
  const h = Math.floor(x / 60);
  const r = x % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

const SOURCE_PILL = {
  audiobook: { label: 'Audiobook', cls: 'bg-purple-500/15 text-purple-200 border-purple-400/30', icon: Headphones },
  cinematic: { label: 'Cinematic', cls: 'bg-pink-500/15 text-pink-200 border-pink-400/30', icon: Film },
  ebook:     { label: 'Ebook',     cls: 'bg-amber-500/15 text-amber-200 border-amber-400/30', icon: BookOpen },
  text:      { label: 'Text',      cls: 'bg-indigo-500/15 text-indigo-200 border-indigo-400/30', icon: FileText },
};

const Tabs = ({ active, onChange, audioCount, textCount }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5" data-testid="breakdown-tabs">
    <TabBtn id="level"  label="Level"   Icon={Crown}      active={active === 'level'}  onClick={() => onChange('level')}  cls="bg-emerald-600/25 text-emerald-100 border-emerald-400/40 ring-emerald-400/40" />
    <TabBtn id="coins"  label="Coins"   Icon={Coins}      active={active === 'coins'}  onClick={() => onChange('coins')}  cls="bg-amber-600/25 text-amber-100 border-amber-400/40 ring-amber-400/40" />
    <TabBtn id="audio"  label="Audio"   Icon={Headphones} active={active === 'audio'}  onClick={() => onChange('audio')}  count={audioCount} cls="bg-purple-600/25 text-purple-100 border-purple-400/40 ring-purple-400/40" />
    <TabBtn id="words"  label="Words"   Icon={BookOpen}   active={active === 'words'}  onClick={() => onChange('words')}  count={textCount}  cls="bg-amber-600/25 text-amber-100 border-amber-400/40 ring-amber-400/40" />
  </div>
);

const TabBtn = ({ id, label, Icon, active, onClick, cls, count }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`breakdown-tab-${id}`}
    className={`flex items-center justify-center gap-1.5 py-3 rounded-xl border text-sm font-bold transition-colors ${
      active ? `${cls} ring-1` : 'bg-slate-800/50 text-slate-300 border-slate-700 hover:bg-slate-800'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count != null && (
      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-900/60 tabular-nums">{count}</span>
    )}
  </button>
);

const Row = ({ pill, title, primary, secondary, xp, testid }) => {
  const P = pill ? SOURCE_PILL[pill] : null;
  const Icon = P?.icon;
  return (
    <div
      className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-800/60 border border-slate-700"
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-lg bg-slate-900/60 border border-slate-700 flex items-center justify-center flex-shrink-0">
        {Icon ? <Icon className="w-5 h-5 text-slate-300" /> : <BookOpen className="w-5 h-5 text-slate-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm truncate">{title}</p>
        <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
          {P && (
            <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${P.cls}`}>
              {P.label}
            </span>
          )}
          {secondary && <span>{secondary}</span>}
        </p>
      </div>
      <div className="text-right tabular-nums flex-shrink-0">
        <p className="text-white font-black text-base leading-none">{primary}</p>
        {xp != null && xp > 0 && (
          <p
            className="text-[11px] font-bold text-amber-300 mt-1 inline-flex items-center gap-1 justify-end"
            data-testid={`${testid}-xp`}
            title="XP rewarded for this upload — visible to writer & admins only"
          >
            <Sparkles className="w-3 h-3" /> {fmtNum(xp)} XP
          </p>
        )}
      </div>
    </div>
  );
};

const WriterJourneyBreakdown = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const allowedTabs = ['audio', 'words', 'level', 'coins'];
  const initial = allowedTabs.includes(params.get('type')) ? params.get('type') : 'audio';
  const [tab, setTab] = useState(initial);
  const [data, setData] = useState(null);
  const [xpItems, setXpItems] = useState({});
  const [xpSeries, setXpSeries] = useState({});
  const [loading, setLoading] = useState(true);
  const [evolving, setEvolving] = useState(false);

  const fetchAll = async () => {
    const [bRes, xRes] = await Promise.allSettled([
      authFetch(`${BACKEND_URL}/api/writer/journey/breakdown`),
      authFetch(`${BACKEND_URL}/api/writer/content-xp`),
    ]);
    const d = bRes.status === 'fulfilled' && bRes.value.ok
      ? await bRes.value.json()
      : { audio_items: [], text_items: [], totals: {} };
    const xj = xRes.status === 'fulfilled' && xRes.value.ok
      ? await xRes.value.json()
      : { items: {}, series: {} };
    setData(d);
    setXpItems(xj.items || {});
    setXpSeries(xj.series || {});
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      if (u.role !== 'writer' && u.role !== 'admin') {
        navigate('/profile');
        return;
      }
      await fetchAll();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleEvolve = async () => {
    if (evolving) return;
    setEvolving(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/writer/evolve`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Evolution failed.');
        return;
      }
      toast.success(`Evolved to Scribe Lv ${d.newLevel}!`);
      await fetchAll();
    } catch {
      toast.error('Network error during evolution.');
    } finally {
      setEvolving(false);
    }
  };

  const onTab = (t) => {
    setTab(t);
    setParams({ type: t }, { replace: true });
  };

  const items = useMemo(() => (tab === 'words' ? (data?.text_items || []) : (data?.audio_items || [])), [tab, data]);
  const totals = data?.totals || {};
  // Sum of every per-item XP earned across the whole library — surfaced
  // at the very top of the page per user spec (May 31 2026).
  const totalXP = useMemo(
    () => Object.values(xpItems).reduce((acc, v) => acc + Number(v || 0), 0),
    [xpItems],
  );

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-gray-400">Loading breakdown…</p></div>;
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="writer-journey-breakdown-page">
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => navigate('/writer-journey')} className="p-2 rounded-lg hover:bg-slate-800" data-testid="breakdown-back">
          <ArrowLeft className="w-4 h-4 text-slate-300" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-300" />
          Journey Breakdown
        </h1>
      </div>

      {/* Total XP hero — the headline number for the whole page. Sits
          above every other stat so the scribe reads "XP earned" first. */}
      <section
        className="relative overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/25 via-orange-500/15 to-fuchsia-500/15 p-5 mb-4"
        data-testid="breakdown-total-xp"
      >
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-amber-300" />
          <span className="text-[11px] uppercase tracking-widest font-bold text-amber-200/80">
            Total XP earned
          </span>
        </div>
        <p className="text-3xl sm:text-4xl font-black text-white tabular-nums">
          {fmtNum(totalXP)} <span className="text-amber-300 text-lg align-middle">XP</span>
        </p>
        <p className="text-[11px] text-amber-100/70 mt-1">
          Across every upload — visible to writer & admins only.
        </p>
      </section>

      {/* Totals card */}
      <section className="grid grid-cols-3 gap-2 mb-5" data-testid="breakdown-totals">
        <div className="rounded-xl bg-purple-900/30 border border-purple-500/30 p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest font-bold text-purple-200/80">Audio</p>
          <p className="text-lg sm:text-xl font-black text-white tabular-nums">{fmtMins(totals.audio_minutes)}</p>
        </div>
        <div className="rounded-xl bg-amber-900/30 border border-amber-500/30 p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest font-bold text-amber-200/80">Words</p>
          <p className="text-lg sm:text-xl font-black text-white tabular-nums">{fmtNum(totals.words)}</p>
        </div>
        <div className="rounded-xl bg-emerald-900/30 border border-emerald-500/30 p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-200/80">Effective</p>
          <p className="text-lg sm:text-xl font-black text-white tabular-nums">{fmtNum(totals.effective_units)}</p>
        </div>
      </section>

      <Tabs
        active={tab}
        onChange={onTab}
        audioCount={data?.audio_items?.length || 0}
        textCount={data?.text_items?.length || 0}
      />

      {(tab === 'audio' || tab === 'words') && (
        items.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8" data-testid="breakdown-empty">
            {tab === 'words'
              ? 'No written uploads yet — publish a story script or an ebook to start counting words.'
              : 'No audio uploads yet — publish an audiobook or cinematic series to start counting minutes.'}
          </p>
        ) : (
          <div className="space-y-2" data-testid="breakdown-items">
            {tab === 'words'
              ? items.map((d) => (
                  <Row
                    key={d.content_id}
                    pill={(d.source || 'ebook').toLowerCase()}
                    title={d.title || 'Untitled'}
                    primary={`${fmtNum(d.word_count)}w`}
                    secondary={`${fmtNum(d.word_count)} words`}
                    xp={xpItems[d.content_id]}
                    testid={`breakdown-item-${d.content_id}`}
                  />
                ))
              : items.map((d) => (
                  <Row
                    key={d.series_id}
                    pill={d.source}
                    title={d.title || 'Untitled'}
                    primary={fmtMins(d.minutes)}
                    secondary={`${d.episode_count} episode${d.episode_count === 1 ? '' : 's'}`}
                    xp={xpSeries[d.series_id]}
                    testid={`breakdown-item-${d.series_id}`}
                  />
                ))}
          </div>
        )
      )}

      {tab === 'level' && data?.level && (
        <LevelSection level={data.level} onEvolve={handleEvolve} evolving={evolving} />
      )}

      {tab === 'coins' && data?.coins && (
        <CoinsSection coins={data.coins} />
      )}
    </main>
  );
};

// ── Level tab — full ladder + pulsing Evolve button at the top.
const LevelSection = ({ level, onEvolve, evolving }) => {
  const ladder = level.ladder || [];
  const idx = ladder.findIndex((l) => l.current);
  const visible = ladder.slice(Math.max(0, idx - 2), Math.max(0, idx - 2) + 30);
  const percent = Math.min(100, Math.round((level.xp_in_current_level / Math.max(1, level.xp_for_next_level)) * 100));

  return (
    <section data-testid="breakdown-level-section">
      <section
        className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-500/20 via-sky-500/15 to-fuchsia-500/15 border-emerald-400/40 p-5 mb-3"
        data-testid="writer-level-hero"
      >
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-4 h-4" />
          <span className="text-[11px] uppercase tracking-widest font-bold opacity-80">
            Scribe Lv {level.current}
          </span>
        </div>
        <p className="text-3xl sm:text-4xl font-black text-white tabular-nums">{level.tier}</p>
        <p className="text-[11px] text-white/70 mt-1">
          {fmtNum(level.xp_in_current_level)} / {fmtNum(level.xp_for_next_level)} banked · {fmtNum(level.total_xp)} lifetime XP
        </p>

        <div className="mt-3 w-full bg-slate-900/40 rounded-full h-2.5">
          <div
            className="h-2.5 rounded-full transition-all"
            style={{ width: `${percent}%`, background: `linear-gradient(90deg, ${level.tier_hex}aa, ${level.tier_hex})` }}
          />
        </div>
        <p className="text-[11px] text-white/70 mt-1.5">{percent}% of the way to Lv {level.current + 1}</p>

        {level.evolution_eligible && (
          <button
            type="button"
            onClick={onEvolve}
            disabled={evolving}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 font-bold text-white animate-pulse-evolve focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:opacity-60"
            style={{ background: `linear-gradient(90deg, ${level.tier_hex}, #f59e0b)` }}
            data-testid="writer-evolve-btn"
          >
            {evolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {evolving ? 'Evolving…' : `Evolve to Lv ${level.current + 1}`}
          </button>
        )}
      </section>

      <h2 className="text-sm font-bold text-slate-300 mt-5 mb-2">The ladder</h2>
      <p className="text-[11px] text-slate-500 mb-3">Each level needs the XP shown. Tier titles change every 10 levels.</p>
      <div className="space-y-1.5" data-testid="writer-level-ladder">
        {visible.map((l) => {
          const isCurrent = l.current;
          const isPast = l.completed;
          return (
            <div
              key={l.level}
              className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                isCurrent
                  ? 'bg-amber-500/15 border-amber-400/50 ring-1 ring-amber-400/40'
                  : isPast
                  ? 'bg-slate-900/50 border-slate-700'
                  : 'bg-slate-800/40 border-slate-700'
              }`}
              data-testid={`writer-level-row-${l.level}`}
            >
              <span
                className="w-9 h-9 rounded-md flex items-center justify-center text-xs font-black flex-shrink-0"
                style={{ background: `${l.tier_hex}22`, color: l.tier_hex, border: `1px solid ${l.tier_hex}55` }}
              >
                {l.level}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${isCurrent ? 'text-white' : isPast ? 'text-slate-300' : 'text-slate-200'}`}>
                  Lv {l.level} · <span style={{ color: l.tier_hex }}>{l.tier}</span>
                </p>
                <p className="text-[11px] text-slate-500">
                  {fmtNum(l.xp_required)} XP this level · {fmtNum(l.xp_cumulative)} cumulative
                </p>
              </div>
              {isPast && <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
              {!isPast && !isCurrent && <Lock className="w-3 h-3 text-slate-500 flex-shrink-0" />}
              {isCurrent && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-200 bg-amber-500/20 border border-amber-400/40 rounded-full px-2 py-0.5">
                  current
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── Coins tab — balance · earnings by source · milestone history.
const CoinsSection = ({ coins }) => {
  const gbp = (coins.balance || 0) * 0.015;
  return (
    <section data-testid="breakdown-coins-section">
      <section
        className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-amber-500/25 via-yellow-500/20 to-orange-500/15 border-amber-400/40 p-5 mb-3"
        data-testid="writer-coins-hero"
      >
        <div className="flex items-center gap-2 mb-1">
          <Coins className="w-4 h-4 text-amber-200" />
          <span className="text-[11px] uppercase tracking-widest font-bold text-amber-200/80">
            Coin balance
          </span>
        </div>
        <p className="text-3xl sm:text-4xl font-black text-white tabular-nums">
          {fmtNum(coins.balance)} <span className="text-amber-200 text-lg align-middle ml-1">coins</span>
        </p>
        <p className="text-[11px] text-amber-100/80 mt-1">
          ≈ £{gbp.toFixed(2)} cashable · {fmtNum(coins.total_earned)} lifetime
        </p>
      </section>

      <h2 className="text-sm font-bold text-slate-300 mt-5 mb-2">Earnings by source</h2>
      <div className="space-y-2 mb-5" data-testid="writer-coins-sources">
        {coins.sources?.map((s) => (
          <div
            key={s.source}
            className="flex items-center justify-between p-3 rounded-xl bg-slate-800/60 border border-slate-700"
            data-testid={`writer-coins-source-${s.source}`}
          >
            <span className="text-sm text-slate-200 capitalize">{s.source.replace(/_/g, ' ')}</span>
            <span className="text-sm font-black text-white tabular-nums">{fmtNum(s.coins)} coins</span>
          </div>
        ))}
        {(!coins.sources || coins.sources.length === 0) && (
          <p className="text-xs text-slate-500 py-3 text-center">No milestone payouts yet.</p>
        )}
      </div>

      <h2 className="text-sm font-bold text-slate-300 mt-5 mb-2">Milestone history</h2>
      <div className="space-y-2" data-testid="writer-coins-events">
        {coins.events?.map((e, i) => {
          const at = e.awarded_at ? new Date(e.awarded_at).toLocaleDateString() : '';
          return (
            <div
              key={`${e.milestone_key}-${i}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/60 border border-slate-700"
              data-testid={`writer-coins-event-${e.milestone_key}-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-400/40 text-amber-200 flex items-center justify-center font-black text-xs flex-shrink-0">
                {e.milestone_key}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold">Milestone {e.milestone_key}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 capitalize">
                  {e.source} · {at}
                  {e.words_at_award ? ` · ${fmtNum(e.words_at_award)} words at award` : ''}
                </p>
              </div>
              <div className="text-right tabular-nums flex-shrink-0">
                <p className="text-white font-black text-base leading-none">+{fmtNum(e.coins)}</p>
                <p className="text-[10px] text-amber-300 mt-1">coins</p>
              </div>
            </div>
          );
        })}
        {(!coins.events || coins.events.length === 0) && (
          <p className="text-xs text-slate-500 py-6 text-center">
            No milestone payouts yet. Publish content to start hitting word/audio milestones.
          </p>
        )}
      </div>
    </section>
  );
};

export default WriterJourneyBreakdown;
